import { AnalyticsEvent, trackAnalytics } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';

export type FeedbackCategory =
  | 'praise'
  | 'idea'
  | 'bug'
  | 'accuracy'
  | 'ui'
  | 'performance'
  | 'pricing'
  | 'data_privacy'
  | 'other';
export type FeedbackContextKind = 'post_analysis' | 'global' | 'admin' | 'other';

/** Optionale aspekt-spezifische Bewertungen (jeweils 1-5 Sterne, 0 = übersprungen). */
export type FeedbackAspectRatings = {
  accuracy?: number;        // Genauigkeit der Analyse
  clarity?: number;         // Verständlichkeit
  speed?: number;           // Geschwindigkeit
  usability?: number;       // Bedienung / UI
};

export type FeedbackSubmitInput = {
  rating: number;
  category: FeedbackCategory;
  message: string;
  context_kind?: FeedbackContextKind | null;
  context_path?: string | null;
  saved_analysis_id?: string | null;
  /** Optional: aspekt-spezifische Bewertungen (in metadata gespeichert). */
  aspect_ratings?: FeedbackAspectRatings;
  /** Optional: NPS 0-10 (Net Promoter Score; in metadata gespeichert). */
  nps?: number | null;
  metadata?: Record<string, unknown>;
};

export type FeedbackSubmitResult = {
  ok: true;
  id?: string;
  created_at?: string;
};

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  praise: 'Lob / hat geholfen',
  idea: 'Idee / Verbesserung',
  bug: 'Fehler / etwas funktioniert nicht',
  accuracy: 'KI-Antwort ungenau oder falsch',
  ui: 'Bedienung / UI',
  performance: 'Geschwindigkeit / Performance',
  pricing: 'Preise / Pakete',
  data_privacy: 'Datenschutz / Sicherheit',
  other: 'Sonstiges',
};

export const FEEDBACK_ASPECT_LABELS: Record<keyof FeedbackAspectRatings, string> = {
  accuracy: 'Genauigkeit der Analyse',
  clarity: 'Verständlichkeit',
  speed: 'Geschwindigkeit',
  usability: 'Bedienung / UI',
};

export const FEEDBACK_MESSAGE_MAX = 4000;

export class FeedbackAuthRequiredError extends Error {
  constructor() {
    super('Anmeldung erforderlich');
    this.name = 'FeedbackAuthRequiredError';
  }
}

/**
 * Schickt Feedback an /api/feedback-submit. Wirft `FeedbackAuthRequiredError`
 * wenn keine Session vorhanden ist — der Aufrufer kann dann einen Login-
 * Prompt anzeigen statt einer Fehlermeldung.
 */
export async function submitFeedback(input: FeedbackSubmitInput): Promise<FeedbackSubmitResult> {
  if (!supabase) {
    throw new FeedbackAuthRequiredError();
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token || token.startsWith('mock-local-dev')) {
    throw new FeedbackAuthRequiredError();
  }

  const path =
    input.context_path != null
      ? input.context_path
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`.slice(0, 512)
        : null;

  const metadata: Record<string, unknown> = {
    app_version: import.meta.env.VITE_APP_VERSION || null,
    user_agent:
      typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 280) : null,
    ...(input.metadata || {}),
  };
  // Aspect-Ratings nur einbetten, wenn mindestens ein Wert > 0 gesetzt ist.
  if (input.aspect_ratings) {
    const cleaned: FeedbackAspectRatings = {};
    for (const k of Object.keys(input.aspect_ratings) as (keyof FeedbackAspectRatings)[]) {
      const v = input.aspect_ratings[k];
      if (typeof v === 'number' && v >= 1 && v <= 5) cleaned[k] = v;
    }
    if (Object.keys(cleaned).length > 0) {
      metadata.aspect_ratings = cleaned;
    }
  }
  if (typeof input.nps === 'number' && input.nps >= 0 && input.nps <= 10) {
    metadata.nps = input.nps;
  }

  const body = {
    rating: input.rating,
    category: input.category,
    message: input.message,
    context_kind: input.context_kind ?? null,
    context_path: path,
    saved_analysis_id: input.saved_analysis_id ?? null,
    metadata,
  };

  const res = await fetch('/api/feedback-submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const json = (await res.json().catch(() => ({}))) as { error?: string; ok?: boolean; id?: string; created_at?: string };
  if (!res.ok) {
    if (res.status === 401) throw new FeedbackAuthRequiredError();
    throw new Error(json.error || `Feedback fehlgeschlagen (${res.status})`);
  }
  trackAnalytics(AnalyticsEvent.feedback_submitted, {
    metadata: {
      rating: input.rating,
      category: input.category,
      context_kind: input.context_kind ?? null,
    },
  });
  return { ok: true, id: json.id, created_at: json.created_at };
}
